import { Member, Contribution, Event, Expense, Town, Benefit } from '../types';
import { sanitizeInput } from './sanitize';
import { exportMembersReportPDF, exportFinancialReportPDF } from './pdfExport';

// Note: Toast notifications are now handled by the global toast context
// Components using these functions should handle their own toast notifications

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const exportToExcel = async (data: any[], filename: string, sheetName: string = 'Sheet1') => {
  try {
    if (data.length === 0) {
      console.warn('No data available to export');
      return;
    }

    const { excelExportService } = await import('../services/ExcelExportService');

    // Use the new Excel service for proper .xlsx files
    const excelBlob = await excelExportService.generateSimpleExcel(data, filename, sheetName);

    // Trigger download
    const url = URL.createObjectURL(excelBlob);
    const link = document.createElement('a');

    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.xlsx`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    console.log(`Successfully exported ${data.length} records to ${filename}.xlsx`);
  } catch (error) {
    console.error('Export error:', error);
    console.error('Failed to export data. Please try again.');
  }
};


export const exportFinancialReport = (
  contributions: Contribution[],
  expenses: Expense[],
  towns: Town[],
  members: Member[],
  selectedTownId?: string
) => {
  try {
    console.log('Preparing financial report...');

    const filteredContributions = selectedTownId && selectedTownId !== 'all'
      ? contributions.filter(c => c.townId === selectedTownId)
      : contributions;

    const filteredExpenses = selectedTownId && selectedTownId !== 'all'
      ? expenses.filter(e => e.townId === selectedTownId)
      : expenses;

    // Track missing member data for logging
    let missingMemberCount = 0;
    const missingMemberIds = new Set<string>();

    const financialData = [
      ...filteredContributions.map(c => {
        const town = towns.find(t => t.id === c.townId);
        const member = members.find(m => m.id === c.memberId);

        // Log missing member data
        if (!member) {
          missingMemberCount++;
          missingMemberIds.add(c.memberId);
          console.warn(`Missing member data for memberId: ${c.memberId}`);
        }

        return {
          Type: member?.name || 'Unknown Member',
          Town: town?.name || 'Unknown',
          Amount: c.amount,
          Category: c.type,
          'Payment Method': c.paymentMethod,
          Date: c.date,
          Status: c.status,
          Reference: c.reference || ''
        };
      }),
      ...filteredExpenses.map(e => {
        const town = towns.find(t => t.id === e.townId);
        return {
          Type: 'Expense',
          Town: town?.name || 'Unknown',
          Amount: -e.amount,
          Category: e.category,
          'Payment Method': 'N/A',
          Date: e.date,
          Status: 'Completed',
          Reference: e.reference || e.beneficiary || ''
        };
      })
    ];

    // Log summary of missing member data
    if (missingMemberCount > 0) {
      console.warn(`Financial report: ${missingMemberCount} contributions missing member data for member IDs: ${Array.from(missingMemberIds).join(', ')}`);
    }

    if (financialData.length === 0) {
      const townName = selectedTownId === 'all' ? 'all towns' : towns.find(t => t.id === selectedTownId)?.name || 'selected town';
      console.warn(`No financial data found for ${townName}`);
      return;
    }

    const filename = selectedTownId && selectedTownId !== 'all'
      ? `Financial_Report_${towns.find(t => t.id === selectedTownId)?.name}`
      : 'Financial_Report_All_Towns';

    exportToExcel(financialData, filename, 'Financial');
  } catch (error) {
    console.error('Financial report error:', error);
    console.error('Failed to generate financial report. Please try again.');
  }
};

export const exportExpenseReport = (
  expenses: Expense[],
  towns: Town[],
  selectedTownId?: string,
  dateRange?: { startDate: string; endDate: string }
) => {
  try {
    console.log('Preparing expense report...');

    let filteredExpenses = selectedTownId && selectedTownId !== 'all'
      ? expenses.filter(e => e.townId === selectedTownId)
      : expenses;

    if (dateRange) {
      filteredExpenses = filteredExpenses.filter(expense => {
        const expenseDate = new Date(expense.date);
        return expenseDate >= new Date(dateRange.startDate) && expenseDate <= new Date(dateRange.endDate);
      });
    }

    if (filteredExpenses.length === 0) {
      const townName = selectedTownId === 'all' ? 'all towns' : towns.find(t => t.id === selectedTownId)?.name || 'selected town';
      const dateInfo = dateRange ? ` between ${dateRange.startDate} and ${dateRange.endDate}` : '';
      console.warn(`No expenses found for ${townName}${dateInfo}`);
      return;
    }

    const reportData = filteredExpenses.map(expense => {
      const town = towns.find(t => t.id === expense.townId);
      return {
        Date: expense.date,
        Town: town?.name || 'Unknown',
        Category: expense.category,
        Amount: expense.amount,
        Description: expense.description,
        Beneficiary: expense.beneficiary || '',
        Reference: expense.reference || '',
        'Approval Status': expense.approvalStatus || 'approved',
        'Approved By': expense.approvedBy || '',
        'Approved Date': expense.approvedDate || ''
      };
    });

    const townName = selectedTownId && selectedTownId !== 'all'
      ? towns.find(t => t.id === selectedTownId)?.name
      : 'All_Towns';

    const dateStr = dateRange
      ? `_${dateRange.startDate}_to_${dateRange.endDate}`
      : '';

    exportToExcel(reportData, `Expense_Report_${townName}${dateStr}`, 'Expenses');
  } catch (error) {
    console.error('Expense report error:', error);
    console.error('Failed to generate expense report. Please try again.');
  }
};

export const exportEventReport = (
  events: Event[],
  towns: Town[],
  selectedTownId?: string,
  dateRange?: { startDate: string; endDate: string }
) => {
  try {
    console.log('Preparing event report...');

    let filteredEvents = selectedTownId && selectedTownId !== 'all'
      ? events.filter(e => e.townId === selectedTownId)
      : events;

    // Only apply date filtering if events have valid dates
    if (dateRange) {
      filteredEvents = filteredEvents.filter(event => {
        if (!event.date || event.date === 'invalid-date') return true;
        const eventDate = new Date(event.date);
        if (isNaN(eventDate.getTime())) return true;
        const startDate = new Date(dateRange.startDate);
        const endDate = new Date(dateRange.endDate);
        return eventDate >= startDate && eventDate <= endDate;
      });
    }

    if (filteredEvents.length === 0) {
      const townName = selectedTownId === 'all' ? 'all towns' : towns.find(t => t.id === selectedTownId)?.name || 'selected town';
      const dateInfo = dateRange ? ` between ${dateRange.startDate} and ${dateRange.endDate}` : '';
      console.warn(`No events found for ${townName}${dateInfo}`);
      return;
    }

    const reportData = filteredEvents.map(event => {
      const town = towns.find(t => t.id === event.townId);
      return {
        Date: event.date === 'invalid-date' ? 'Not Set' : event.date,
        Time: event.time,
        Town: town?.name || 'Unknown',
        Title: event.title,
        Type: event.type,
        Location: event.location,
        Description: event.description || '',
        'Max Attendees': event.maxAttendees || 'Unlimited',
        'Registered': event.attendees?.length || 0,
        'Attendance Rate': event.maxAttendees
          ? `${((event.attendees?.length || 0) / event.maxAttendees * 100).toFixed(1)}%`
          : 'N/A'
      };
    });

    const townName = selectedTownId && selectedTownId !== 'all'
      ? towns.find(t => t.id === selectedTownId)?.name
      : 'All_Towns';

    const dateStr = dateRange
      ? `_${dateRange.startDate}_to_${dateRange.endDate}`
      : '';

    exportToExcel(reportData, `Event_Report_${townName}${dateStr}`, 'Events');
  } catch (error) {
    console.error('Event report error:', error);
    console.error('Failed to generate event report. Please try again.');
  }
};

export const exportBenefitsReport = (
  benefits: Benefit[],
  members: Member[],
  towns: Town[],
  selectedTownId?: string,
  dateRange?: { startDate: string; endDate: string }
) => {
  let filteredBenefits = selectedTownId && selectedTownId !== 'all'
    ? benefits.filter(b => b.townId === selectedTownId)
    : benefits;

  if (dateRange) {
    filteredBenefits = filteredBenefits.filter(benefit => {
      const requestDate = new Date(benefit.requestDate);
      return requestDate >= new Date(dateRange.startDate) && requestDate <= new Date(dateRange.endDate);
    });
  }

  const reportData = filteredBenefits.map(benefit => {
    const member = members.find(m => m.id === benefit.memberId);
    const town = towns.find(t => t.id === benefit.townId);
    return {
      'Request Date': benefit.requestDate,
      'Member Name': member?.name || 'Unknown',
      'Member ID': member?.username || 'Unknown',
      Town: town?.name || 'Unknown',
      Type: benefit.type,
      Amount: benefit.amount,
      Description: benefit.description,
      Status: benefit.status,
      'Approved By': benefit.approvedBy || '',
      'Approved Date': benefit.approvedDate || '',
      'Paid Date': benefit.paidDate || '',
      Reference: benefit.reference || ''
    };
  });

  const townName = selectedTownId && selectedTownId !== 'all'
    ? towns.find(t => t.id === selectedTownId)?.name
    : 'All_Towns';

  const dateStr = dateRange
    ? `_${dateRange.startDate}_to_${dateRange.endDate}`
    : '';

  exportToExcel(reportData, `Benefits_Report_${townName}${dateStr}`, 'Benefits');
};

export const exportAuditReport = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  auditLogs: any[],
  dateRange?: { startDate: string; endDate: string }
) => {
  let filteredLogs = auditLogs;

  if (dateRange) {
    filteredLogs = auditLogs.filter(log => {
      const logDate = new Date(log.timestamp);
      return logDate >= new Date(dateRange.startDate) && logDate <= new Date(dateRange.endDate);
    });
  }

  const reportData = filteredLogs.map(log => ({
    Timestamp: sanitizeInput(log.timestamp),
    User: sanitizeInput(log.userName),
    Action: sanitizeInput(log.action),
    'Entity Type': sanitizeInput(log.entityType),
    'Entity ID': sanitizeInput(log.entityId),
    'Old Data': sanitizeInput(log.oldData || ''),
    'New Data': sanitizeInput(log.newData || '')
  }));

  const dateStr = dateRange
    ? `_${dateRange.startDate}_to_${dateRange.endDate}`
    : '';

  exportToExcel(reportData, `Audit_Report${dateStr}`, 'Audit_Trail');
};

// Enhanced Excel export functions
export const exportMembersReportExcel = async (
  members: Member[],
  towns: Town[],
  selectedTownId: string = 'all',
  additionalData: {
    contributions?: Contribution[];
    events?: Event[];
    benefits?: Benefit[];
    expenses?: Expense[];
  } = {},
  options: { bypassTownFilter?: boolean } = {}
): Promise<void> => {
  try {
    const filteredMembers = (selectedTownId && selectedTownId !== 'all' && !options.bypassTownFilter)
      ? members.filter(m => m.townId === selectedTownId)
      : members;

    if (filteredMembers.length === 0) {
      const townName = selectedTownId === 'all' ? 'all towns' : towns.find(t => t.id === selectedTownId)?.name || 'selected town';
      console.warn(`No members found for ${townName}`);
      return;
    }

    const townName = selectedTownId === 'all'
      ? 'All_Towns'
      : towns.find(t => t.id === selectedTownId)?.name || 'Unknown_Town';

    const filename = `Members_Report_${townName}_${new Date().toISOString().slice(0, 10)}`;

    const { excelExportService } = await import('../services/ExcelExportService');

    // Use Excel service for professional report
    const excelBlob = await excelExportService.generateMembersReport(filteredMembers, towns, selectedTownId, additionalData, options);

    // Trigger download
    const url = URL.createObjectURL(excelBlob);
    const link = document.createElement('a');

    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.xlsx`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    console.log(`Successfully exported members report to ${filename}.xlsx`);
  } catch (error) {
    console.error('Members report Excel error:', error);
    throw error;
  }
};

// Deprecate the old function name but keep it working by redirecting to the new one
export const exportMembersReport = (
  members: Member[],
  towns: Town[],
  selectedTownId: string = 'all',
  additionalData: {
    contributions?: Contribution[];
    events?: Event[];
    benefits?: Benefit[];
    expenses?: Expense[];
  } = {},
  options: { bypassTownFilter?: boolean } = {}
) => {
  return exportMembersReportExcel(members, towns, selectedTownId, additionalData, options);
};

export const exportFinancialReportExcel = async (
  contributions: Contribution[],
  expenses: Expense[],
  towns: Town[],
  members: Member[],
  selectedTownId?: string
): Promise<void> => {
  try {
    const filteredContributions = selectedTownId && selectedTownId !== 'all'
      ? contributions.filter(c => c.townId === selectedTownId)
      : contributions;

    const filteredExpenses = selectedTownId && selectedTownId !== 'all'
      ? expenses.filter(e => e.townId === selectedTownId)
      : expenses;

    if (filteredContributions.length === 0 && filteredExpenses.length === 0) {
      const townName = selectedTownId === 'all' ? 'all towns' : towns.find(t => t.id === selectedTownId)?.name || 'selected town';
      console.warn(`No financial data found for ${townName}`);
      return;
    }

    const townName = selectedTownId === 'all'
      ? 'All_Towns'
      : towns.find(t => t.id === selectedTownId)?.name || 'Unknown_Town';

    const filename = `Financial_Report_${townName}_${new Date().toISOString().slice(0, 10)}`;

    const { excelExportService } = await import('../services/ExcelExportService');

    // Use Excel service for professional report
    const excelBlob = await excelExportService.generateFinancialReport(filteredContributions, filteredExpenses, towns, members, selectedTownId);

    // Trigger download
    const url = URL.createObjectURL(excelBlob);
    const link = document.createElement('a');

    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.xlsx`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    console.log(`Successfully exported financial report to ${filename}.xlsx`);
  } catch (error) {
    console.error('Financial report Excel error:', error);
    throw new Error('Failed to generate financial report Excel. Please try again.');
  }
};

// Export all enhanced export functions
export const enhancedExports = {
  exportToExcel,
  exportMembersReportPDF,
  exportFinancialReportPDF,
  exportMembersReportExcel,
  exportFinancialReportExcel
};